import { useState, useEffect, useCallback } from 'react';
import { Activity, ActivityService } from '../services/activityService';

export const useActivity = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Charger les activités récentes pour un utilisateur
  const loadUserActivities = useCallback(async (userId: string, limit: number = 10) => {
    setLoading(true);
    setError(null);
    try {
      const userActivities = await ActivityService.getRecentActivities(userId, limit);
      setActivities(userActivities);
    } catch (err) {
      setError('Erreur lors du chargement des activités');
      console.error('Erreur chargement activités utilisateur:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les activités globales récentes
  const loadGlobalActivities = useCallback(async (limit: number = 20) => {
    setLoading(true);
    setError(null);
    try {
      const globalActivities = await ActivityService.getGlobalRecentActivities(limit);
      setActivities(globalActivities);
    } catch (err) {
      setError('Erreur lors du chargement des activités');
      console.error('Erreur chargement activités globales:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Enregistrer une activité de like
  const recordLike = useCallback(async (actorId: string, postId: string, actorUsername: string, actorDisplayName: string) => {
    try {
      await ActivityService.recordLikeActivity(actorId, postId, actorUsername, actorDisplayName);
    } catch (err) {
      console.error('Erreur enregistrement like:', err);
    }
  }, []);

  // Enregistrer une activité de commentaire
  const recordComment = useCallback(async (actorId: string, postId: string, content: string, actorUsername: string, actorDisplayName: string) => {
    try {
      await ActivityService.recordCommentActivity(actorId, postId, content, actorUsername, actorDisplayName);
    } catch (err) {
      console.error('Erreur enregistrement commentaire:', err);
    }
  }, []);

  // Enregistrer une activité de follow
  const recordFollow = useCallback(async (actorId: string, targetId: string, actorUsername: string, actorDisplayName: string) => {
    try {
      await ActivityService.recordFollowActivity(actorId, targetId, actorUsername, actorDisplayName);
    } catch (err) {
      console.error('Erreur enregistrement follow:', err);
    }
  }, []);

  // Enregistrer une activité de post
  const recordPost = useCallback(async (actorId: string, postId: string, actorUsername: string, actorDisplayName: string) => {
    try {
      await ActivityService.recordPostActivity(actorId, postId, actorUsername, actorDisplayName);
    } catch (err) {
      console.error('Erreur enregistrement post:', err);
    }
  }, []);

  // Enregistrer une découverte de token
  const recordTokenDiscovery = useCallback(async (actorId: string, tokenSymbol: string, actorUsername: string, actorDisplayName: string) => {
    try {
      await ActivityService.recordTokenDiscoveryActivity(actorId, tokenSymbol, actorUsername, actorDisplayName);
    } catch (err) {
      console.error('Erreur enregistrement découverte token:', err);
    }
  }, []);

  // Formater le temps relatif
  const getRelativeTime = useCallback((date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'À l\'instant';
    if (diffInMinutes < 60) return `Il y a ${diffInMinutes} min`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Il y a ${diffInHours}h`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) return `Il y a ${diffInDays}j`;
    
    const diffInMonths = Math.floor(diffInDays / 30);
    return `Il y a ${diffInMonths}m`;
  }, []);

  // Formater le message d'activité
  const formatActivityMessage = useCallback((activity: Activity) => {
    const displayName = activity.actorDisplayName || `@${activity.actorUsername}`;
    
    switch (activity.type) {
      case 'like':
        return `${displayName} a aimé un post`;
      case 'comment':
        return `${displayName} a commenté un post`;
      case 'follow':
        return `${displayName} vous suit maintenant`;
      case 'post':
        return `${displayName} a publié un nouveau post`;
      case 'token_discovery':
        return `Nouveau token découvert : $${activity.tokenSymbol}`;
      default:
        return `${displayName} a fait une action`;
    }
  }, []);

  return {
    activities,
    loading,
    error,
    loadUserActivities,
    loadGlobalActivities,
    recordLike,
    recordComment,
    recordFollow,
    recordPost,
    recordTokenDiscovery,
    getRelativeTime,
    formatActivityMessage
  };
};
